import React, { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    apiUploadBrandingLogo,
    apiRemoveBrandingLogo,
    apiBrandingLogoUrl,
    formatApiError,
} from "@/lib/api";
import {
    LayoutDashboard,
    FolderKanban,
    CalendarClock,
    ClipboardList,
    PlaneTakeoff,
    Users,
    LogOut,
    ChevronDown,
    Moon,
    CalendarRange,
    FileBarChart2,
    Image as ImageIcon,
    Upload,
    Trash2,
    Building2,
    ShieldCheck,
    ListTodo,
} from "lucide-react";

// Navigation is organized into groups. `type: "link"` renders a standalone link.
// `type: "group"` renders a dropdown menu with `items[]` inside. Role visibility for
// each item is preserved via the same `admin` / `lead` flags as before.
const NAV_GROUPS = [
    {
        type: "link",
        key: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        to: "/",
    },
    {
        type: "group",
        key: "operations",
        label: "Operations",
        icon: CalendarClock,
        items: [
            { key: "shifts", label: "Shifts", icon: CalendarClock, to: "/admin/shifts", admin: true },
            { key: "roster", label: "Roster", icon: CalendarRange, to: "/roster" },
            { key: "handovers", label: "Handovers", icon: ClipboardList, to: "/handovers" },
            { key: "leaves", label: "Leaves", icon: PlaneTakeoff, to: "/leaves" },
        ],
    },
    {
        type: "group",
        key: "projects",
        label: "Projects",
        icon: FolderKanban,
        items: [
            { key: "customers", label: "Customers", icon: Building2, to: "/admin/customers", admin: true },
            { key: "projects", label: "Projects", icon: FolderKanban, to: "/admin/projects", admin: true },
            { key: "tasks", label: "Tasks", icon: ListTodo, to: "/tasks" },
        ],
    },
    {
        type: "link",
        key: "reports",
        label: "Reports",
        icon: FileBarChart2,
        to: "/reports",
        lead: true,
    },
    {
        type: "group",
        key: "admin",
        label: "Admin",
        icon: ShieldCheck,
        items: [
            { key: "users", label: "Users", icon: Users, to: "/admin/users", admin: true },
        ],
    },
];

function itemAllowedFor(role, item) {
    // Client only sees dashboard/roster/handovers content
    if (role === "Client") {
        return ["dashboard", "roster", "handovers"].includes(item.key);
    }
    if (role === "Consultant") {
        return !item.admin && !item.lead;
    }
    if (role === "Project Manager" || role === "Team Lead") {
        return !item.admin;
    }
    // Super Admin / Admin see everything
    return true;
}

/**
 * Filter the top-level nav groups for the current role.
 * Standalone links: kept only if the item passes `itemAllowedFor`.
 * Groups: keep only the sub-items the role can see; drop the group entirely if empty.
 */
function visibleGroupsFor(role) {
    return NAV_GROUPS.map((g) => {
        if (g.type === "link") {
            return itemAllowedFor(role, g) ? g : null;
        }
        const items = g.items.filter((it) => itemAllowedFor(role, it));
        return items.length ? { ...g, items } : null;
    }).filter(Boolean);
}

function isPathActive(location, to) {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname === to || location.pathname.startsWith(to + "/");
}

function TopLink({ group, active, location }) {
    const Icon = group.icon;
    const isActive =
        active === group.key || isPathActive(location, group.to);
    const cls = `text-[13px] px-3 h-8 rounded-md flex items-center gap-1.5 transition-colors ${
        isActive
            ? "bg-white text-[#070F26]"
            : "text-white/75 hover:text-white hover:bg-white/10"
    }`;
    return (
        <Link to={group.to} data-testid={`nav-${group.key}`} className={cls}>
            <Icon className="h-3.5 w-3.5" />
            {group.label}
        </Link>
    );
}

function TopGroup({ group, active, location }) {
    const Icon = group.icon;
    // The group is highlighted when any child route is active OR when the `active`
    // prop from the current page matches any child key.
    const groupActive =
        group.items.some(
            (it) => it.key === active || isPathActive(location, it.to),
        );
    const btnCls = `text-[13px] px-3 h-8 rounded-md flex items-center gap-1.5 transition-colors ${
        groupActive
            ? "bg-white text-[#070F26]"
            : "text-white/75 hover:text-white hover:bg-white/10"
    }`;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid={`nav-group-${group.key}`}
                    className={btnCls}
                >
                    <Icon className="h-3.5 w-3.5" />
                    {group.label}
                    <ChevronDown className="h-3 w-3 ml-0.5 opacity-70" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                className="min-w-[200px]"
                data-testid={`nav-group-${group.key}-menu`}
            >
                {group.items.map((it) => {
                    const ItemIcon = it.icon;
                    const isActive =
                        active === it.key || isPathActive(location, it.to);
                    return (
                        <DropdownMenuItem key={it.key} asChild>
                            <Link
                                to={it.to}
                                data-testid={`nav-${it.key}`}
                                className={`flex items-center gap-2 text-[13px] cursor-pointer w-full ${
                                    isActive ? "font-semibold text-[#0072BC]" : ""
                                }`}
                            >
                                <ItemIcon className="h-3.5 w-3.5" />
                                {it.label}
                            </Link>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function Initials({ name }) {
    const parts = (name || "").trim().split(/\s+/).slice(0, 2);
    const s =
        parts.map((p) => p[0]?.toUpperCase()).join("") || (name || "?")[0];
    return <>{s}</>;
}

export default function Layout({ children, active }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const groups = visibleGroupsFor(user?.role);
    const [logoAvailable, setLogoAvailable] = useState(true);
    const [logoStamp, setLogoStamp] = useState(String(Date.now()));
    const [brandingOpen, setBrandingOpen] = useState(false);
    const canManageBranding = user?.role === "Super Admin" || user?.role === "Admin";
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    async function handleLogoUpload(file) {
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Logo must be 2 MB or smaller");
            return;
        }
        setUploading(true);
        try {
            await apiUploadBrandingLogo(file);
            setLogoStamp(String(Date.now()));
            setLogoAvailable(true);
            toast.success("Logo updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    }

    async function handleLogoRemove() {
        setUploading(true);
        try {
            await apiRemoveBrandingLogo();
            setLogoAvailable(false);
            setLogoStamp(String(Date.now()));
            toast.success("Logo removed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    }

    return (
        <div
            className="min-h-screen flex flex-col bg-ntt-surface"
            data-testid="app-shell"
        >
            <header
                className="bg-[#070F26] text-white sticky top-0 z-30 border-b border-black/20"
                data-testid="app-header"
            >
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <Link
                            to="/"
                            data-testid="brand-link"
                            className="flex items-center gap-3"
                        >
                            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#0072BC] to-[#003a63] flex items-center justify-center shadow-[0_8px_22px_rgba(0,114,188,0.4)]">
                                <Moon className="h-6 w-6 text-white" />
                            </div>
                            <div className="leading-tight">
                                <div className="text-[22px] font-semibold tracking-tight">
                                    NightOwl
                                </div>
                                <div className="text-[12px] text-white/65 -mt-0.5">
                                    By NTT Data
                                </div>
                            </div>
                        </Link>
                        <nav
                            className="hidden md:flex items-center gap-1"
                            data-testid="primary-nav"
                        >
                            {groups.map((g) => {
                                if (g.type === "link") {
                                    return (
                                        <TopLink
                                            key={g.key}
                                            group={g}
                                            active={active}
                                            location={location}
                                        />
                                    );
                                }
                                return (
                                    <TopGroup
                                        key={g.key}
                                        group={g}
                                        active={active}
                                        location={location}
                                    />
                                );
                            })}
                        </nav>
                    </div>

                    <div className="flex items-center gap-4">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="user-menu-trigger"
                                    type="button"
                                    className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-white/10 transition-colors"
                                >
                                    <span className="h-7 w-7 rounded-full bg-[#0072BC] text-white text-[12px] font-semibold flex items-center justify-center">
                                        <Initials name={user?.full_name} />
                                    </span>
                                    <span className="hidden md:block text-[13px] text-white/90 max-w-[140px] truncate">
                                        {user?.full_name}
                                    </span>
                                    <ChevronDown className="h-3.5 w-3.5 text-white/70" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="w-64"
                                data-testid="user-menu"
                            >
                                <DropdownMenuLabel>
                                    <div className="text-[13px] font-medium text-[#070F26]">
                                        {user?.full_name}
                                    </div>
                                    <div className="text-[11px] text-ntt-muted truncate">
                                        {user?.email}
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem disabled>
                                    <span className="text-[12px]">
                                        Role · {user?.role}
                                    </span>
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled>
                                    <span className="text-[12px]">
                                        Timezone · {user?.timezone || "—"}
                                    </span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {canManageBranding && (
                                    <DropdownMenuItem
                                        onClick={() => setBrandingOpen(true)}
                                        data-testid="branding-menu-btn"
                                        className="text-[13px] cursor-pointer"
                                    >
                                        <ImageIcon className="h-4 w-4 mr-2" />
                                        Branding & Logo
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={logout}
                                    data-testid="logout-btn"
                                    className="text-[13px] cursor-pointer"
                                >
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            {canManageBranding && (
                <Dialog open={brandingOpen} onOpenChange={setBrandingOpen}>
                    <DialogContent
                        className="max-w-lg"
                        data-testid="branding-dialog"
                    >
                        <DialogHeader>
                            <DialogTitle>Branding & Logo</DialogTitle>
                            <DialogDescription>
                                The logo shows in the app header and on every PDF
                                report. Recommended: PNG or SVG under 2 MB with a
                                transparent background.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="border border-ntt-border rounded-md h-28 flex items-center justify-center bg-[#F4F6F9]" data-testid="branding-preview">
                                {logoAvailable ? (
                                    <img
                                        src={apiBrandingLogoUrl(logoStamp)}
                                        alt="Current logo"
                                        data-testid="branding-preview-img"
                                        className="max-h-20 max-w-[300px] object-contain"
                                        onError={() => setLogoAvailable(false)}
                                    />
                                ) : (
                                    <span className="text-[12px] text-ntt-muted">
                                        No logo set. The header slot stays empty until you upload one.
                                    </span>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                className="hidden"
                                data-testid="branding-file-input"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleLogoUpload(f);
                                    e.target.value = "";
                                }}
                            />
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    data-testid="branding-upload-btn"
                                    className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                                >
                                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                                    {uploading
                                        ? "Uploading…"
                                        : logoAvailable
                                            ? "Replace logo"
                                            : "Upload logo"}
                                </Button>
                                {logoAvailable && (
                                    <Button
                                        variant="outline"
                                        onClick={handleLogoRemove}
                                        disabled={uploading}
                                        data-testid="branding-remove-btn"
                                        className="h-9 text-red-600 hover:text-red-700"
                                    >
                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                        Remove
                                    </Button>
                                )}
                            </div>
                            <p className="text-[11px] text-ntt-muted">
                                Accepted formats: PNG, JPEG, SVG, WEBP. Max 2 MB.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setBrandingOpen(false)}
                                data-testid="branding-close-btn"
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
                {children}
            </main>

            <footer className="bg-[#070F26] text-white/55 text-[12px]">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <span>© NightOwl · Internal Tool</span>
                    <span>By NTT Data</span>
                </div>
            </footer>
        </div>
    );
}
